import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  doc, setDoc, updateDoc, getDocs, collection, query, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, Divider, Snackbar, Alert, Tooltip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
  ToggleButtonGroup, ToggleButton, Tabs, Tab, Grid,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import {
  buildSignatureHTML, buildPlainText, loadSignatureContext,
  EMPTY_COMPANY, EMPTY_USER, SIGNATURE_PLATFORMS as PLATFORMS,
} from '../utils/signatureHtml';


// ── Component ─────────────────────────────────────────────────────────────────
export default function Signature() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [companyData, setCompanyData] = useState(EMPTY_COMPANY);
  const [deptData, setDeptData]       = useState({ id: '', name: '', deptTitle: '', roleLabel: '' });
  const [userData, setUserData]       = useState(EMPTY_USER);

  const [platform, setPlatform] = useState('desktop');
  const [layout, setLayout]     = useState('portrait');
  const [showSource, setShowSource] = useState(false);
  const [copied, setCopied] = useState(''); // 'html' | 'text' | ''

  const [canEditCompany, setCanEditCompany] = useState(false);
  const [canEditDept, setCanEditDept]       = useState(false);
  const [myDeptId, setMyDeptId]             = useState('');
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingDept, setSavingDept]       = useState(false);
  const [savingUser, setSavingUser]       = useState(false);
  const [toast, setToast] = useState('');

  // ── Review tab ───────────────────────────────────────────────────────────────
  const [mainTab, setMainTab]           = useState(0);
  const [reviewUsers, setReviewUsers]   = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [copiedReview, setCopiedReview] = useState(''); // userId
  const [reviewDeptFilter, setReviewDeptFilter] = useState(''); // '' = alla
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewSortByDept, setReviewSortByDept] = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);

    const { company, dept, user, userDoc } = await loadSignatureContext(currentUser);

    const isSuperadmin   = userDoc.role === 'superadmin';
    const isMarketingMgr = !!userDoc.canManageMarketing;
    const isDeptMgr      = userDoc.role === 'ao_ansvarig' || !!userDoc.isDepartmentManager;

    setCanEditCompany(isSuperadmin || isMarketingMgr);
    setCanEditDept(isSuperadmin || isMarketingMgr || isDeptMgr);
    setMyDeptId(userDoc.departmentId || '');

    setUserData(user);
    setCompanyData(company);
    if (dept.id) setDeptData(dept);

    setLoading(false);
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  // ── Load review users (lazy – called when switching to tab 1) ───────────────
  async function loadReviewUsers() {
    if (reviewLoaded) return;
    setReviewLoading(true);

    // Load all departments for roleLabel lookup
    const deptSnap = await getDocs(collection(db, 'departments'));
    const deptMap = {};
    deptSnap.docs.forEach(d => { deptMap[d.id] = d.data(); });

    // Scope: marknadsansvarig → all users, AO-ansvarig → own dept only
    let usersSnap;
    if (canEditCompany) {
      usersSnap = await getDocs(collection(db, 'users'));
    } else {
      usersSnap = await getDocs(
        query(collection(db, 'users'), where('departmentId', '==', myDeptId))
      );
    }

    const loaded = usersSnap.docs.map(d => {
      const u = d.data();
      const dept = deptMap[u.departmentId] || {};
      return {
        id: d.id,
        displayName: u.displayName || u.email || d.id,
        deptName: dept.name || '',
        userData: {
          displayName:      u.displayName || '',
          mobile:               u.signatureMobile                || '',
          office:               u.signatureOffice                || '',
          email:                (u.email || '').toLowerCase(),
          personalLinkedIn:     u.signaturePersonalLinkedIn      || '',
          showPersonalLinkedIn: u.signatureShowPersonalLinkedIn  ?? false,
        },
        deptData: {
          id:        u.departmentId || '',
          name:      dept.name || '',
          offerCode: dept.offerCode || '',
          deptTitle: dept.signatureDeptTitle  || '',
          roleLabel: dept.signatureRoleLabel  || '',
        },
      };
    });

    // Sort by name
    loaded.sort((a, b) => a.displayName.localeCompare(b.displayName, 'sv'));
    setReviewUsers(loaded);
    setReviewLoaded(true);
    setReviewLoading(false);
  }

  function handleMainTabChange(_, v) {
    setMainTab(v);
    if (v === 1) loadReviewUsers();
  }

  async function handleCopyReview(u) {
    const html = buildSignatureHTML(companyData, u.deptData, u.userData, platform, layout);
    try {
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
    } catch {
      try { await navigator.clipboard.writeText(html); } catch {}
    }
    setCopiedReview(u.id);
    setTimeout(() => setCopiedReview(''), 2500);
  }

  // ── Save handlers ───────────────────────────────────────────────────────────
  async function handleSaveCompany() {
    setSavingCompany(true);
    // Spara aldrig logoUrl – den hämtas alltid från companies-dokumentet
    const { logoUrl: _skip, ...dataToSave } = companyData;
    await setDoc(doc(db, 'marketingSettings', 'signatureCompany'), dataToSave);
    setSavingCompany(false);
    setToast('Företagsinformation sparad');
  }

  async function handleSaveDept() {
    if (!deptData.id) return;
    setSavingDept(true);
    await updateDoc(doc(db, 'departments', deptData.id), {
      signatureDeptTitle: deptData.deptTitle,
      signatureRoleLabel: deptData.roleLabel,
    });
    setSavingDept(false);
    setToast('Affärsområdesinformation sparad');
  }

  async function handleSaveUser() {
    setSavingUser(true);
    await updateDoc(doc(db, 'users', currentUser.uid), {
      mobile:                         userData.mobile,   // kanoniskt fält – används av bl.a. offertmodulen
      signatureMobile:                userData.mobile,
      signatureOffice:                userData.office,
      signaturePersonalLinkedIn:      userData.personalLinkedIn,
      signatureShowPersonalLinkedIn:  userData.showPersonalLinkedIn,
    });
    setSavingUser(false);
    setToast('Din information sparad');
  }

  // ── Copy handlers ───────────────────────────────────────────────────────────
  async function handleCopyHTML() {
    const html = buildSignatureHTML(companyData, deptData, userData, platform, layout);
    try {
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
    } catch {
      // Fallback: plain text copy of the HTML source
      try { await navigator.clipboard.writeText(html); } catch {}
    }
    setCopied('html');
    setTimeout(() => setCopied(''), 2500);
  }

  async function handleCopyText() {
    await navigator.clipboard.writeText(buildPlainText(companyData, deptData, userData));
    setCopied('text');
    setTimeout(() => setCopied(''), 2500);
  }

  // ── Derived ─────────────────────────────────────────────────────────────────
  const signatureHTML = buildSignatureHTML(companyData, deptData, userData, platform, layout);
  const iframeDoc = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{margin:16px;background:#fff;}</style>
</head><body>${signatureHTML}</body></html>`;

  // ── Shared save-button style ─────────────────────────────────────────────────
  const darkBtn = { bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } };
  const orangeBtn = { bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">Mailsignatur</Typography>
          <Typography variant="caption" sx={{ color: '#aaa', display: { xs: 'none', sm: 'block' } }}>
            Skapa och exportera signaturer för Outlook
          </Typography>
        </Box>
        {(canEditCompany || canEditDept) && (
          <Tabs
            value={mainTab}
            onChange={handleMainTabChange}
            sx={{ ml: { xs: 1, sm: 3 },
              '& .MuiTab-root': { color: '#aaa', minHeight: 56,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 } },
              '& .Mui-selected': { color: 'white !important' },
              '& .MuiTabs-indicator': { bgcolor: '#FF6B00' },
            }}>
            <Tab label="Min signatur" />
            <Tab label={canEditCompany ? 'Granska alla' : 'Granska AO'} />
          </Tabs>
        )}
      </Box>

      {/* ── Tab 0: Min signatur ──────────────────────────────────────────── */}
      {mainTab === 0 && (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' } }}>

          {/* ── LEFT: Form ────────────────────────────────────────────────── */}
          <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}>

            {/* Företagsnivå */}
            <Paper sx={{ mb: 2 }}>
              <Accordion defaultExpanded={canEditCompany}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold" variant="body2">Företagsinformation</Typography>
                    <Chip
                      label={canEditCompany ? 'Gäller alla medarbetare' : 'Sätts av marknadsansvarig'}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 18,
                        bgcolor: canEditCompany ? '#E8F5E9' : '#f5f5f5',
                        color: canEditCompany ? '#2E7D32' : 'text.secondary' }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Logotyp
                      </Typography>
                      {companyData.logoUrl ? (
                        <Box sx={{ mt: 1 }}>
                          <Box component="img" src={companyData.logoUrl}
                            alt="Logo" sx={{ maxHeight: 48, display: 'block', mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Hämtad från företagets inställningar
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          Ingen logotyp uppladdad – gör det under Företagsinställningar
                        </Typography>
                      )}
                    </Box>
                    <TextField fullWidth size="small" label="Logotyp bredd (px)" type="number"
                      disabled={!canEditCompany} value={companyData.logoWidth}
                      onChange={e => setCompanyData(p => ({ ...p, logoWidth: parseInt(e.target.value) || 80 }))} />
                    <TextField fullWidth size="small" label="Webbplats"
                      disabled={!canEditCompany} value={companyData.website}
                      helperText="T.ex. www.tikab.com"
                      onChange={e => setCompanyData(p => ({ ...p, website: e.target.value }))} />
                    <TextField fullWidth size="small" label="Adress"
                      disabled={!canEditCompany} value={companyData.address}
                      onChange={e => setCompanyData(p => ({ ...p, address: e.target.value }))} />
                    <TextField fullWidth size="small" label="Tagline"
                      disabled={!canEditCompany} value={companyData.tagline}
                      onChange={e => setCompanyData(p => ({ ...p, tagline: e.target.value }))} />
                    <TextField fullWidth size="small" label="LinkedIn (företagets profil)"
                      disabled={!canEditCompany} value={companyData.companyLinkedIn}
                      placeholder="https://www.linkedin.com/company/..."
                      onChange={e => setCompanyData(p => ({ ...p, companyLinkedIn: e.target.value }))} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Namnfärg</Typography>
                        <input type="color" value={companyData.nameColor}
                          disabled={!canEditCompany}
                          onChange={e => setCompanyData(p => ({ ...p, nameColor: e.target.value }))}
                          style={{ display: 'block', width: '100%', height: 36,
                            border: '1px solid #ddd', borderRadius: 4,
                            cursor: canEditCompany ? 'pointer' : 'not-allowed' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Taglinefärg</Typography>
                        <input type="color" value={companyData.taglineColor}
                          disabled={!canEditCompany}
                          onChange={e => setCompanyData(p => ({ ...p, taglineColor: e.target.value }))}
                          style={{ display: 'block', width: '100%', height: 36,
                            border: '1px solid #ddd', borderRadius: 4,
                            cursor: canEditCompany ? 'pointer' : 'not-allowed' }} />
                      </Box>
                    </Box>
                    {canEditCompany && (
                      <Button variant="contained" size="small"
                        startIcon={savingCompany ? <CircularProgress size={14} /> : <SaveIcon />}
                        onClick={handleSaveCompany} disabled={savingCompany} sx={darkBtn}>
                        Spara företagsinformation
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Affärsområdesnivå */}
            <Paper sx={{ mb: 2 }}>
              <Accordion defaultExpanded={canEditDept}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold" variant="body2">
                      Affärsområde{deptData.name ? `: ${deptData.name}` : ''}
                    </Typography>
                    <Chip
                      label={canEditDept ? 'Gäller alla i AO' : 'Sätts av AO-ansvarig'}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 18,
                        bgcolor: canEditDept ? '#E3F2FD' : '#f5f5f5',
                        color: canEditDept ? '#1565C0' : 'text.secondary' }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField fullWidth size="small" label="Titel / Beskrivning (AO-specifik)"
                      disabled={!canEditDept} value={deptData.deptTitle}
                      helperText={canEditDept
                        ? 'T.ex. "Datadrivna Projekt & Digitala Arbetssätt" – gäller alla i AO'
                        : 'Sätts av AO-ansvarig'}
                      onChange={e => setDeptData(p => ({ ...p, deptTitle: e.target.value }))} />
                    <TextField fullWidth size="small" label="Rolltitel (AO-specifik)"
                      disabled={!canEditDept} value={deptData.roleLabel}
                      helperText={canEditDept
                        ? 'T.ex. "Affärsområdesansvarig" – gäller alla i AO'
                        : 'Sätts av AO-ansvarig'}
                      onChange={e => setDeptData(p => ({ ...p, roleLabel: e.target.value }))} />
                    {canEditDept && (
                      <Button variant="contained" size="small"
                        startIcon={savingDept ? <CircularProgress size={14} /> : <SaveIcon />}
                        onClick={handleSaveDept} disabled={savingDept || !deptData.id} sx={darkBtn}>
                        Spara affärsområdesinformation
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Individnivå */}
            <Paper>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold" variant="body2">Din information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField fullWidth size="small" label="Namn" value={userData.displayName}
                      disabled helperText="Ändras i din profil" />
                    <TextField fullWidth size="small" label="E-post" value={userData.email}
                      disabled helperText="Ändras i din profil (alltid gemener)" />
                    <TextField fullWidth size="small" label="Titel / Beskrivning"
                      value={deptData.deptTitle}
                      disabled helperText="Sätts av AO-ansvarig" />
                    <TextField fullWidth size="small" label="Rolltitel"
                      value={deptData.roleLabel}
                      disabled helperText="Sätts av AO-ansvarig" />
                    <TextField fullWidth size="small"
                      label="Mobilnummer (landsnummer + mobil)"
                      placeholder="+46 70 000 00 00"
                      helperText="Förifyllt från Konsultprofil (Telefon)"
                      value={userData.mobile}
                      onChange={e => setUserData(p => ({ ...p, mobile: e.target.value }))} />
                    <TextField fullWidth size="small"
                      label="Kontorsnummer (landsnummer + kontor)"
                      placeholder="+46 8 000 000 00"
                      value={userData.office}
                      onChange={e => setUserData(p => ({ ...p, office: e.target.value }))} />
                    <Box>
                      <TextField fullWidth size="small" label="Min LinkedIn-profil (personlig)"
                        value={userData.personalLinkedIn}
                        helperText="Förifyllt från Konsultprofil"
                        placeholder="https://www.linkedin.com/in/..."
                        onChange={e => setUserData(p => ({ ...p, personalLinkedIn: e.target.value }))} />
                      <FormControlLabel
                        sx={{ mt: 0.5, ml: 0.5 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!userData.showPersonalLinkedIn}
                            onChange={e => setUserData(p => ({ ...p, showPersonalLinkedIn: e.target.checked }))}
                          />
                        }
                        label={<Typography variant="caption">Visa i signatur</Typography>}
                      />
                    </Box>
                    <Button variant="contained" size="small"
                      startIcon={savingUser ? <CircularProgress size={14} /> : <SaveIcon />}
                      onClick={handleSaveUser} disabled={savingUser} sx={orangeBtn}>
                      Spara min information
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>

          {/* ── RIGHT: Preview ────────────────────────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 3 }}>

              {/* Controls row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                {/* Platform */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold"
                    sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Plattform
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive value={platform}
                    onChange={(_, v) => {
                      if (!v) return;
                      setPlatform(v);
                      if (v === 'android' || v === 'ios') setLayout('portrait');
                    }}>
                    {PLATFORMS.map(pl => (
                      <ToggleButton key={pl.key} value={pl.key}
                        sx={{ px: { xs: 1, sm: 1.5 }, fontSize: '0.72rem', textTransform: 'none' }}>
                        {pl.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Layout – döljs vid mobila plattformar */}
                {platform !== 'android' && platform !== 'ios' && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold"
                    sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Layout
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive
                    value={layout} onChange={(_, v) => v && setLayout(v)}>
                    <ToggleButton value="portrait"
                      sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>
                      Stående
                    </ToggleButton>
                    <ToggleButton value="landscape"
                      sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>
                      Liggande
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                )}

                {/* Action buttons */}
                <Box sx={{ ml: { xs: 0, sm: 'auto' }, display: 'flex', gap: 1,
                  alignItems: 'center', flexWrap: 'wrap' }}>
                  <Tooltip title="Visa HTML-källkod">
                    <IconButton size="small" onClick={() => setShowSource(p => !p)}
                      sx={{ bgcolor: showSource ? '#1a1a1a' : 'transparent',
                        color: showSource ? 'white' : 'inherit',
                        border: '1px solid', borderColor: 'divider' }}>
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" variant="outlined"
                    startIcon={copied === 'text' ? <CheckIcon /> : <ContentCopyIcon />}
                    onClick={handleCopyText}
                    sx={{ fontSize: '0.72rem', textTransform: 'none', minWidth: 130 }}>
                    {copied === 'text' ? 'Kopierad!' : 'Kopiera ren text'}
                  </Button>
                  <Button size="small" variant="contained"
                    startIcon={copied === 'html' ? <CheckIcon /> : <ContentCopyIcon />}
                    onClick={handleCopyHTML}
                    sx={{ ...orangeBtn, fontSize: '0.72rem', textTransform: 'none', minWidth: 150 }}>
                    {copied === 'html' ? 'Kopierad!' : 'Kopiera signatur'}
                  </Button>
                </Box>
              </Box>

              {/* Platform tip */}
              {(platform === 'android' || platform === 'ios') && (
                <Box sx={{ bgcolor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 1,
                  px: 2, py: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Outlook-appen på mobil stödjer ofta bara ren text som signatur.
                    Använd <strong>Kopiera ren text</strong> och klistra in i appens signaturfält,
                    eller testa den formaterade varianten – vissa versioner hanterar enkel HTML.
                  </Typography>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Preview */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Förhandsgranskning – {PLATFORMS.find(p => p.key === platform)?.label}
                </Typography>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white' }}>
                  <iframe
                    ref={iframeRef}
                    title="Signaturförhandsgranskning"
                    srcDoc={iframeDoc}
                    style={{ width: '100%', border: 'none', display: 'block', minHeight: 180 }}
                    onLoad={e => {
                      try {
                        const body = e.target.contentWindow.document.body;
                        e.target.style.height = (body.scrollHeight + 32) + 'px';
                      } catch {}
                    }}
                  />
                </Box>
              </Box>

              {/* HTML source */}
              {showSource && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      HTML-källkod
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Klistra in i OWA → Inställningar → Signatur → källkodsläge
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth multiline rows={14}
                    value={signatureHTML}
                    slotProps={{
                      input: { readOnly: true,
                        style: { fontFamily: 'monospace', fontSize: '0.72rem' } },
                    }}
                    sx={{ bgcolor: '#fafafa' }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>
      )}

      {/* ── Tab 1: Granska signaturer ─────────────────────────────────────── */}
      {mainTab === 1 && (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Platform + layout controls */}
        <Paper sx={{ p: 2, mb: 3 }}>
          {/* Rad 1: Plattform + Layout */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Plattform
              </Typography>
              <ToggleButtonGroup size="small" exclusive value={platform}
                onChange={(_, v) => v && setPlatform(v)}>
                {PLATFORMS.map(pl => (
                  <ToggleButton key={pl.key} value={pl.key}
                    sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>
                    {pl.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Layout
              </Typography>
              <ToggleButtonGroup size="small" exclusive value={layout}
                onChange={(_, v) => v && setLayout(v)}>
                <ToggleButton value="portrait"
                  sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>Stående</ToggleButton>
                <ToggleButton value="landscape"
                  sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>Liggande</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          {/* Divider */}
          <Divider sx={{ mb: 2 }} />
          {/* Rad 2: Sök + Filter + Sortering + Räknare */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Sök namn..."
              value={reviewSearch} onChange={e => setReviewSearch(e.target.value)}
              sx={{ width: 200 }} />
            {canEditCompany && (() => {
              const depts = [...new Map(
                reviewUsers.filter(u => u.deptName)
                  .map(u => [u.deptData.id, { name: u.deptName, offerCode: u.deptData.offerCode }])
              ).entries()].sort((a, b) => a[1].name.localeCompare(b[1].name, 'sv'));
              return depts.length > 0 ? (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Affärsområde</InputLabel>
                  <Select value={reviewDeptFilter} label="Affärsområde"
                    onChange={e => setReviewDeptFilter(e.target.value)}>
                    <MenuItem value="">Alla</MenuItem>
                    {depts.map(([id, d]) => (
                      <MenuItem key={id} value={id}>
                        {d.offerCode ? `${d.offerCode} – ${d.name}` : d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null;
            })()}
            <Tooltip title={reviewSortByDept ? 'Klicka för att sortera på namn' : 'Klicka för att sortera på AO'}>
              <Chip
                label={reviewSortByDept ? 'Sorterat: AO' : 'Sorterat: Namn'}
                size="small"
                onClick={() => setReviewSortByDept(v => !v)}
                sx={{ cursor: 'pointer', height: 32, fontSize: '0.75rem',
                  bgcolor: reviewSortByDept ? '#1a1a1a' : 'transparent',
                  color: reviewSortByDept ? 'white' : 'text.primary' }}
                variant={reviewSortByDept ? 'filled' : 'outlined'}
              />
            </Tooltip>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {(() => {
                const q = reviewSearch.toLowerCase();
                return reviewUsers.filter(u =>
                  (!reviewDeptFilter || u.deptData.id === reviewDeptFilter) &&
                  (!q || u.displayName.toLowerCase().includes(q))
                ).length;
              })()} / {reviewUsers.length} personer
            </Typography>
          </Box>
        </Paper>

        {reviewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {(() => {
              const q = reviewSearch.toLowerCase();
              let list = reviewUsers.filter(u =>
                (!reviewDeptFilter || u.deptData.id === reviewDeptFilter) &&
                (!q || u.displayName.toLowerCase().includes(q))
              );
              if (reviewSortByDept) {
                list = [...list].sort((a, b) => {
                  const ac = a.deptData.offerCode || a.deptName || '';
                  const bc = b.deptData.offerCode || b.deptName || '';
                  return ac.localeCompare(bc, 'sv') || a.displayName.localeCompare(b.displayName, 'sv');
                });
              }
              return list;
            })().map(u => {
              const html = buildSignatureHTML(companyData, u.deptData, u.userData, platform, layout);
              const iDoc = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{margin:14px;background:#fff;}</style>
</head><body>${html}</body></html>`;
              return (
                <Grid item xs={12} sm={6} lg={4} key={u.id}>
                  <Paper sx={{ overflow: 'hidden' }}>
                    {/* Card header */}
                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid #eee',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{u.displayName}</Typography>
                        {u.deptName && (
                          <Tooltip title={u.deptName}>
                            <Chip label={deptCode(u.deptData)} size="small"
                              sx={{ fontSize: '0.6rem', height: 16, mt: 0.3,
                                bgcolor: '#1a1a1a', color: 'white' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Button size="small" variant="outlined"
                        startIcon={copiedReview === u.id ? <CheckIcon /> : <ContentCopyIcon />}
                        onClick={() => handleCopyReview(u)}
                        sx={{ fontSize: '0.68rem', textTransform: 'none', minWidth: 100 }}>
                        {copiedReview === u.id ? 'Kopierad!' : 'Kopiera'}
                      </Button>
                    </Box>
                    {/* Signature preview */}
                    <Box sx={{ bgcolor: 'white', px: 1 }}>
                      <iframe
                        title={`sig-${u.id}`}
                        srcDoc={iDoc}
                        style={{ width: '100%', border: 'none', display: 'block', minHeight: 160 }}
                        onLoad={e => {
                          try {
                            const body = e.target.contentWindow.document.body;
                            e.target.style.height = (body.scrollHeight + 28) + 'px';
                          } catch {}
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
